import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { HttpGraphyClient, type GraphyClient } from "./api/client";
import { createEmbeddedAuth } from "./auth/embeddedAuth";
import { ConnectingState } from "./components/states";
import "./styles.css";

// Production uses the HTTP client against the FastAPI backend behind the gateway
// (ADR-web-stack). In dev only, with no API base configured, fall back to the
// clearly-marked in-memory fixture so the UI renders locally. The fixture is
// never bundled as authorized customer data.
const apiBase = import.meta.env.VITE_API_BASE as string | undefined;
// The platform origin that embeds this app and relays the session (postMessage,
// origin-pinned). Required in a production build; in dev the fixture needs none.
const parentOrigin = import.meta.env.VITE_PARENT_ORIGIN as string | undefined;
const projectId = (import.meta.env.VITE_PROJECT_ID as string | undefined) ?? "p-1";

// Give up waiting for the relayed session after this long and show the honest
// "not connected" state (e.g. opened standalone, not inside the platform).
const CONNECT_TIMEOUT_MS = 8000;

const root = ReactDOM.createRoot(document.getElementById("root")!);

function renderApp(client: GraphyClient) {
  root.render(
    <React.StrictMode>
      <App client={client} projectId={projectId} />
    </React.StrictMode>,
  );
}

function renderConnecting(failed: boolean) {
  root.render(
    <React.StrictMode>
      <ConnectingState failed={failed} />
    </React.StrictMode>,
  );
}

async function boot() {
  // Dev-only: no API base => in-memory fixture, render immediately.
  if (!apiBase) {
    if (import.meta.env.DEV) {
      const { devClient } = await import("./api/devFixture");
      renderApp(devClient);
      return;
    }
    // No API base in a production build is a misconfiguration — fail loud rather
    // than silently serving fixture data.
    throw new Error("VITE_API_BASE is required in production builds");
  }
  if (!parentOrigin) {
    throw new Error("VITE_PARENT_ORIGIN is required in production builds");
  }

  // Production: the app is embedded; the platform relays the Bearer token. Hold
  // the connecting state until the first token arrives, then mount the app. The
  // client reads the token lazily per request, so later refreshes are picked up.
  const auth = createEmbeddedAuth(parentOrigin);
  const client = new HttpGraphyClient(apiBase, auth.getToken, auth.requestToken);

  if (auth.getToken()) {
    renderApp(client);
    return;
  }

  renderConnecting(false);
  let mounted = false;
  const unsub = auth.subscribe((token) => {
    if (!token || mounted) return;
    mounted = true;
    unsub();
    window.clearTimeout(timer);
    renderApp(client);
  });
  const timer = window.setTimeout(() => {
    if (!mounted) renderConnecting(true);
  }, CONNECT_TIMEOUT_MS);
}

boot();
